import React from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {format} from 'date-fns';
import {useGate, useUnit} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useHistory, useParams} from 'react-router-dom';

import {ROUTES} from '@app/routing';
import {colors} from '@app/styles/theme';
import {EMPTY_VALUE as EMPTY} from '@consts/common';
import {STATUS_COLOR_LIST} from '@consts/statuses';
import {
    DocumentTemplateDetailsGate,
    documentTemplateDetailsModel,
    MessageTemplateDetailsGate,
    messageTemplateDetailsModel,
} from '@entities/document-templates/model';
import {
    ContentTemplateDto,
    DictionaryItem,
    LocalizedTitle,
    MessageTemplateDto,
} from '@entities/document-templates/types';
import {Button, ButtonVariant} from '@shared/ui/Button';
import {Checkbox} from '@shared/ui/Checkbox';
import {Icon} from '@shared/ui/Icon';
import {Input} from '@shared/ui/Input';
import {Modal} from '@shared/ui/Modal';
import {ContentContainer, StatusWrapper} from '@shared/ui/PageComponents';
import {Select, SelectOption} from '@shared/ui/Select';
import {mapTableColumns, Table} from '@shared/ui/Table2.0';
import {Textarea} from '@shared/ui/Textarea';
import {Title} from '@shared/ui/Title';

import {
    DocumentTemplateFormValues,
    getDocumentTemplateSchema,
    getMessageTemplateSchema,
    MessageTemplateFormValues,
} from './schema';
import {
    Actions,
    BackButton,
    BodyPreview,
    CheckboxRow,
    Field,
    FieldError,
    Form,
    HeaderCard,
    InfoGrid,
    InfoItem,
    InfoLabel,
    InfoValue,
    MessagesHeader,
    ModalFrame,
    SectionCard,
    Wrapper,
} from './styled';

const localizeTitle = (title: LocalizedTitle | null | undefined, language: string) => {
    if (!title) return EMPTY;
    return title[language] ?? title.en ?? EMPTY;
};

const localizeFormValue = (title: LocalizedTitle | null | undefined, language: string) => {
    if (!title) return '';
    return title[language] ?? title.en ?? '';
};

const formatDate = (value?: string | null) => {
    if (!value) return EMPTY;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return EMPTY;
    return format(date, 'dd.MM.yyyy');
};

const statusColor = (code?: string | null) =>
    code === 'ENABLED' ? STATUS_COLOR_LIST.GREEN : STATUS_COLOR_LIST.GRAY_ALT;

const messageColumns = (t: (key: string) => string) =>
    mapTableColumns([
        {id: 'channel', header: t('page_audit.page_document-templates.channel'), width: 300},
        {id: 'language', header: t('page_audit.page_document-templates.language'), width: 150},
        {id: 'subject', header: t('page_audit.page_document-templates.subject'), minWidth: 220},
        {id: 'status', header: t('page_audit.terms.status'), width: 150},
    ]);

const contentColumns = (t: (key: string) => string) =>
    mapTableColumns([
        {id: 'name', header: t('page_audit.page_document-templates.filename'), minWidth: 300},
        {
            id: 'type',
            header: t('page_audit.page_document-templates.body-content-type'),
            width: 190,
        },
        {id: 'status', header: t('page_audit.terms.status'), width: 150},
    ]);

const defaultValues: DocumentTemplateFormValues = {
    code: '',
    title: '',
    description: '',
    violationType: '',
    kind: '',
    isEnabled: false,
};

const defaultMessageValues: MessageTemplateFormValues = {
    channel: '',
    language: '',
    subject: '',
    bodyContentType: '',
    body: '',
    isEnabled: false,
};

export const DocumentTemplateDetails = () => {
    const {notificationTemplateId} = useParams<{notificationTemplateId: string}>();
    const {t, i18n} = useTranslation();
    const history = useHistory();
    const model = useUnit(documentTemplateDetailsModel);
    const [isEdit, setIsEdit] = React.useState(false);
    const isNewEntity = notificationTemplateId === 'new';

    useGate(DocumentTemplateDetailsGate, {notificationTemplateId});

    const {template} = model.state;
    const violationType = model.state.violationTypes.find(
        (item) => item.id === template?.violationType,
    );
    const status = model.state.activeStatuses.find(
        (item) => item.id === template?.status || item.code === template?.status,
    );
    const isEnabled = status?.code === 'ENABLED';

    const translateValidates = React.useCallback(
        (key: string, replace?: Record<string, unknown>) =>
            t(`page_audit.page_document-templates.validation.${key}`, replace),
        [i18n.language, t],
    );
    const schema = React.useMemo(() => getDocumentTemplateSchema(translateValidates, isNewEntity), [
        isNewEntity,
        translateValidates,
    ]);
    const {control, handleSubmit, errors, reset} = useForm<DocumentTemplateFormValues>({
        resolver: yupResolver(schema),
        mode: 'all',
        defaultValues,
    });

    const formValues = React.useMemo<DocumentTemplateFormValues>(
        () =>
            template
                ? {
                      title: localizeFormValue(template.title, i18n.language),
                      code: template.code ?? '',
                      description: localizeFormValue(template.description, i18n.language),
                      violationType: template.violationType ?? '',
                      kind: template.kind?.id ?? '',
                      isEnabled,
                  }
                : defaultValues,
        [i18n.language, isEnabled, template],
    );

    React.useEffect(() => {
        reset(formValues);
        setIsEdit(isNewEntity);
    }, [formValues, isNewEntity, reset]);

    const violationTypeOptions = React.useMemo(
        () =>
            model.state.violationTypes.map((item) => ({
                label: localizeTitle(item.title, i18n.language),
                value: item.id,
            })),
        [i18n.language, model.state.violationTypes],
    );

    const kindOptions = React.useMemo(
        () =>
            model.state.notificationKinds.map((item) => ({
                label: localizeTitle(item.title, i18n.language),
                value: item.id,
            })),
        [i18n.language, model.state.notificationKinds],
    );

    const onSubmit = React.useCallback(
        async (values: DocumentTemplateFormValues) => {
            const statusToSave = model.state.activeStatuses.find((item) =>
                values.isEnabled ? item.code === 'ENABLED' : item.code === 'DISABLED',
            );
            const payload = {
                code: template?.code ?? values.code.trim(),
                title: template
                    ? {
                          ...(template.title ?? {}),
                          [i18n.language]: values.title.trim(),
                      }
                    : {
                          en: values.title.trim(),
                          ru: values.title.trim(),
                          [i18n.language]: values.title.trim(),
                      },
                description: template
                    ? {
                          ...(template.description ?? {}),
                          [i18n.language]: values.description.trim(),
                      }
                    : {
                          en: values.description.trim(),
                          ru: values.description.trim(),
                          [i18n.language]: values.description.trim(),
                      },
                violationType: values.violationType,
                kind: {id: values.kind},
                status: statusToSave?.id ?? template?.status ?? '',
            };

            const savedTemplate = template
                ? await model.saveTemplate({id: template.id, payload})
                : await model.createTemplate(payload);

            if (!template) {
                history.replace(
                    ROUTES.PREFERENCES_DOCUMENT_TEMPLATE_DETAILS.replace(
                        ':notificationTemplateId',
                        savedTemplate.id,
                    ),
                );
            }
            setIsEdit(false);
        },
        [history, i18n.language, model, template],
    );

    const messages = React.useMemo(
        () =>
            (model.state.messageTemplates?.data ?? [])
                .filter((row: MessageTemplateDto) => {
                    const language = model.state.languages.find((item) => item.id === row.language);
                    return language?.code !== 'ru';
                })
                .map((row: MessageTemplateDto) => {
                    const language = model.state.languages.find((item) => item.id === row.language);
                    const status = model.state.activeStatuses.find(
                        (item: DictionaryItem) =>
                            item.id === row.status || item.code === row.status,
                    );

                    return {
                        ...row,
                        channel: localizeTitle(row.channel?.title, i18n.language),
                        language: localizeTitle(language?.title, i18n.language),
                        subject: row.subject || EMPTY,
                        status: (
                            <StatusWrapper color={statusColor(status?.code)}>
                                {localizeTitle(status?.title, i18n.language)}
                            </StatusWrapper>
                        ),
                    };
                }),
        [
            i18n.language,
            model.state.activeStatuses,
            model.state.languages,
            model.state.messageTemplates,
        ],
    );

    return (
        <ContentContainer
            status={model.state.pending && !template && !isNewEntity ? 'loading' : 'default'}
            noPaddings
        >
            <Wrapper>
                <HeaderCard>
                    <BackButton
                        type="button"
                        onClick={() => history.push(ROUTES.PREFERENCES_DOCUMENT_TEMPLATES)}
                    >
                        <Icon name="arrowLeft" size={16} color={colors.brandPrimary} />
                        {t('page_audit.page_dda.tabs.document-templates')}
                    </BackButton>
                    <MessagesHeader>
                        <Title size={4}>
                            {isNewEntity
                                ? t('page_audit.page_document-templates.new-document-template')
                                : t('page_audit.terms.document-template-name-and-version-full', {
                                      name: localizeTitle(template?.title, i18n.language),
                                      version: formatDate(template?.updated),
                                  })}
                        </Title>
                        {isEdit ? (
                            <Actions>
                                <Button
                                    size="sm"
                                    type="button"
                                    variant={ButtonVariant.Outline}
                                    disabled={model.state.pending}
                                    onClick={() => {
                                        if (isNewEntity) {
                                            history.push(ROUTES.PREFERENCES_DOCUMENT_TEMPLATES);
                                            return;
                                        }

                                        reset(formValues);
                                        setIsEdit(false);
                                    }}
                                >
                                    {t('actions.cancel')}
                                </Button>
                                <Button
                                    size="sm"
                                    type="submit"
                                    form="document-template-form"
                                    isLoading={model.state.pending}
                                    disabled={model.state.pending}
                                >
                                    {t('actions.save')}
                                </Button>
                            </Actions>
                        ) : (
                            <Actions>
                                <Button
                                    size="sm"
                                    type="button"
                                    variant={ButtonVariant.Outline}
                                    disabled={!template || model.state.pending}
                                    onClick={() => setIsEdit(true)}
                                >
                                    {t('actions.edit')}
                                </Button>
                            </Actions>
                        )}
                        {status ? (
                            <StatusWrapper color={statusColor(status.code)}>
                                {localizeTitle(status.title, i18n.language)}
                            </StatusWrapper>
                        ) : null}
                    </MessagesHeader>
                </HeaderCard>

                <Form id="document-template-form" onSubmit={handleSubmit(onSubmit)} noValidate>
                    <SectionCard>
                        <InfoGrid>
                            <InfoItem>
                                {isNewEntity ? (
                                    <Field>
                                        <Controller
                                            name="code"
                                            control={control}
                                            render={({onChange, value, name}) => (
                                                <Input
                                                    size="sm"
                                                    label={t(
                                                        'page_audit.page_document-templates.code',
                                                    )}
                                                    name={name}
                                                    value={value}
                                                    onChange={onChange}
                                                    hasError={Boolean(errors.code)}
                                                    isRequired
                                                    disabled={model.state.pending}
                                                />
                                            )}
                                        />
                                        {errors.code && (
                                            <FieldError>{errors.code.message}</FieldError>
                                        )}
                                    </Field>
                                ) : (
                                    <>
                                        <InfoLabel>
                                            {t('page_audit.page_document-templates.code')}
                                        </InfoLabel>
                                        <InfoValue>{template?.code || EMPTY}</InfoValue>
                                    </>
                                )}
                            </InfoItem>
                            {isEdit ? (
                                <>
                                    <Field>
                                        <Controller
                                            name="title"
                                            control={control}
                                            render={({onChange, value, name}) => (
                                                <Input
                                                    size="sm"
                                                    label={t(
                                                        'page_audit.page_document-templates.title',
                                                    )}
                                                    name={name}
                                                    value={value}
                                                    onChange={onChange}
                                                    hasError={Boolean(errors.title)}
                                                    isRequired
                                                    disabled={model.state.pending}
                                                />
                                            )}
                                        />
                                        {errors.title && (
                                            <FieldError>{errors.title.message}</FieldError>
                                        )}
                                    </Field>
                                    <Field>
                                        <Controller
                                            name="description"
                                            control={control}
                                            render={({onChange, value, name}) => (
                                                <Input
                                                    size="sm"
                                                    label={t(
                                                        'page_audit.page_document-templates.description',
                                                    )}
                                                    name={name}
                                                    value={value}
                                                    onChange={onChange}
                                                    hasError={Boolean(errors.description)}
                                                    isRequired
                                                    disabled={model.state.pending}
                                                />
                                            )}
                                        />
                                        {errors.description && (
                                            <FieldError>{errors.description.message}</FieldError>
                                        )}
                                    </Field>
                                    <Field>
                                        <Controller
                                            name="violationType"
                                            control={control}
                                            render={({onChange, value}) => (
                                                <Select
                                                    size="sm"
                                                    menuPlacement="top"
                                                    label={t('page_audit.terms.violation-type')}
                                                    options={violationTypeOptions}
                                                    value={violationTypeOptions.find(
                                                        (item) => item.value === value,
                                                    )}
                                                    onChange={(option: unknown) =>
                                                        onChange(
                                                            (option as SelectOption<string> | null)
                                                                ?.value ?? '',
                                                        )
                                                    }
                                                    hasError={Boolean(errors.violationType)}
                                                    isRequired
                                                    disabled={model.state.pending}
                                                />
                                            )}
                                        />
                                        {errors.violationType && (
                                            <FieldError>{errors.violationType.message}</FieldError>
                                        )}
                                    </Field>
                                    <Field>
                                        <Controller
                                            name="kind"
                                            control={control}
                                            render={({onChange, value}) => (
                                                <Select
                                                    size="sm"
                                                    menuPlacement="top"
                                                    label={t('page_audit.terms.notification-type')}
                                                    options={kindOptions}
                                                    value={kindOptions.find(
                                                        (item) => item.value === value,
                                                    )}
                                                    onChange={(option: unknown) =>
                                                        onChange(
                                                            (option as SelectOption<string> | null)
                                                                ?.value ?? '',
                                                        )
                                                    }
                                                    hasError={Boolean(errors.kind)}
                                                    isRequired
                                                    disabled={model.state.pending}
                                                />
                                            )}
                                        />
                                        {errors.kind && (
                                            <FieldError>{errors.kind.message}</FieldError>
                                        )}
                                    </Field>
                                    <Field>
                                        <Controller
                                            name="isEnabled"
                                            control={control}
                                            render={({onChange, value, name}) => (
                                                <CheckboxRow>
                                                    <Checkbox
                                                        name={name}
                                                        checked={Boolean(value)}
                                                        disabled={model.state.pending}
                                                        onChange={(event) =>
                                                            onChange(event.target.checked)
                                                        }
                                                    />
                                                    {t('actions.activate')}
                                                </CheckboxRow>
                                            )}
                                        />
                                    </Field>
                                </>
                            ) : (
                                <>
                                    <InfoItem>
                                        <InfoLabel>
                                            {t('page_audit.page_document-templates.title')}
                                        </InfoLabel>
                                        <InfoValue>
                                            {localizeTitle(template?.title, i18n.language)}
                                        </InfoValue>
                                    </InfoItem>
                                    <InfoItem>
                                        <InfoLabel>
                                            {t('page_audit.page_document-templates.description')}
                                        </InfoLabel>
                                        <InfoValue>
                                            {localizeTitle(template?.description, i18n.language)}
                                        </InfoValue>
                                    </InfoItem>
                                    <InfoItem>
                                        <InfoLabel>
                                            {t('page_audit.terms.violation-type')}
                                        </InfoLabel>
                                        <InfoValue>
                                            {localizeTitle(violationType?.title, i18n.language)}
                                        </InfoValue>
                                    </InfoItem>
                                    <InfoItem>
                                        <InfoLabel>
                                            {t('page_audit.terms.notification-type')}
                                        </InfoLabel>
                                        <InfoValue>
                                            {localizeTitle(template?.kind?.title, i18n.language)}
                                        </InfoValue>
                                    </InfoItem>
                                </>
                            )}
                        </InfoGrid>
                    </SectionCard>
                </Form>

                {!isNewEntity && (
                    <SectionCard>
                        <MessagesHeader>
                            <Title size={3}>
                                {t('page_audit.page_document-templates.messages')}
                            </Title>
                        </MessagesHeader>
                        <Table
                            columns={messageColumns(t)}
                            data={messages}
                            isLoading={model.state.pending}
                            pageCount={Math.ceil(
                                (model.state.messageTemplates?.total ?? 0) /
                                    model.state.messagePageSize,
                            )}
                            pageIndex={model.state.messagePageNumber}
                            totalRowsCount={model.state.messageTemplates?.total ?? 0}
                            onPageChange={model.messagePageNumberChanged}
                            onPageSizeChange={model.messagePageSizeChanged}
                            onRowClick={(row) =>
                                history.push(
                                    ROUTES.PREFERENCES_MESSAGE_TEMPLATE_DETAILS.replace(
                                        ':notificationTemplateId',
                                        notificationTemplateId,
                                    ).replace(':messageTemplateId', row.original.id),
                                )
                            }
                        />
                    </SectionCard>
                )}
            </Wrapper>
        </ContentContainer>
    );
};

export const MessageTemplateDetails = () => {
    const {notificationTemplateId, messageTemplateId} = useParams<{
        notificationTemplateId: string;
        messageTemplateId: string;
    }>();
    const {t, i18n} = useTranslation();
    const history = useHistory();
    const model = useUnit(messageTemplateDetailsModel);
    const [isEdit, setIsEdit] = React.useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);

    useGate(MessageTemplateDetailsGate, {notificationTemplateId, messageTemplateId});

    const {messageTemplate, template} = model.state;
    const status = model.state.activeStatuses.find(
        (item) => item.id === messageTemplate?.status || item.code === messageTemplate?.status,
    );
    const isEnabled = status?.code === 'ENABLED';

    const translateValidates = React.useCallback(
        (key: string, replace?: Record<string, unknown>) =>
            t(`page_audit.page_document-templates.validation.${key}`, replace),
        [i18n.language, t],
    );
    const schema = React.useMemo(() => getMessageTemplateSchema(translateValidates), [
        translateValidates,
    ]);
    const {control, handleSubmit, errors, reset} = useForm<MessageTemplateFormValues>({
        resolver: yupResolver(schema),
        mode: 'all',
        defaultValues: defaultMessageValues,
    });

    const formValues = React.useMemo<MessageTemplateFormValues>(
        () =>
            messageTemplate
                ? {
                      channel: messageTemplate.channel?.id ?? '',
                      language: messageTemplate.language ?? '',
                      subject: messageTemplate.subject ?? '',
                      bodyContentType: messageTemplate.bodyContentType?.id ?? '',
                      body: messageTemplate.body ?? '',
                      isEnabled,
                  }
                : defaultMessageValues,
        [isEnabled, messageTemplate],
    );

    React.useEffect(() => {
        reset(formValues);
        setIsEdit(false);
    }, [formValues, reset]);

    const channelOptions = React.useMemo(
        () =>
            model.state.messageChannels.map((item) => ({
                label: localizeTitle(item.title, i18n.language),
                value: item.id,
            })),
        [i18n.language, model.state.messageChannels],
    );

    const languageOptions = React.useMemo(
        () =>
            model.state.languages.map((item) => ({
                label: localizeTitle(item.title, i18n.language),
                value: item.id,
            })),
        [i18n.language, model.state.languages],
    );

    const contentTypeOptions = React.useMemo(
        () =>
            model.state.contentTypes.map((item) => ({
                label: localizeTitle(item.title, i18n.language),
                value: item.id,
            })),
        [i18n.language, model.state.contentTypes],
    );

    const language = model.state.languages.find((item) => item.id === messageTemplate?.language);

    const onSubmit = React.useCallback(
        async (values: MessageTemplateFormValues) => {
            const statusToSave = model.state.activeStatuses.find((item) =>
                values.isEnabled ? item.code === 'ENABLED' : item.code === 'DISABLED',
            );

            await model.saveMessageTemplate({
                id: messageTemplateId,
                payload: {
                    notificationTemplate: {id: notificationTemplateId},
                    channel: {id: values.channel},
                    subject: values.subject.trim(),
                    body: values.body.trim(),
                    language: values.language,
                    bodyContentType: {id: values.bodyContentType},
                    status: statusToSave?.id ?? messageTemplate?.status ?? '',
                },
            });
            setIsEdit(false);
        },
        [messageTemplate?.status, messageTemplateId, model, notificationTemplateId],
    );

    const contents = React.useMemo(
        () =>
            (model.state.contentTemplates?.data ?? []).map((row: ContentTemplateDto) => {
                const status = model.state.activeStatuses.find(
                    (item: DictionaryItem) => item.id === row.status || item.code === row.status,
                );

                return {
                    ...row,
                    name: row.name || EMPTY,
                    type: localizeTitle(row.type?.title, i18n.language),
                    status: (
                        <StatusWrapper color={statusColor(status?.code)}>
                            {localizeTitle(status?.title, i18n.language)}
                        </StatusWrapper>
                    ),
                };
            }),
        [i18n.language, model.state.activeStatuses, model.state.contentTemplates],
    );

    return (
        <ContentContainer
            status={model.state.pending && !messageTemplate ? 'loading' : 'default'}
            noPaddings
        >
            <Wrapper>
                <HeaderCard>
                    <BackButton
                        type="button"
                        onClick={() =>
                            history.push(
                                ROUTES.PREFERENCES_DOCUMENT_TEMPLATE_DETAILS.replace(
                                    ':notificationTemplateId',
                                    notificationTemplateId,
                                ),
                            )
                        }
                    >
                        <Icon name="arrowLeft" size={16} color={colors.brandPrimary} />
                        {t('page_audit.page_document-templates.document-template')}
                    </BackButton>
                    <MessagesHeader>
                        <Title size={4}>
                            {t('page_audit.page_document-templates.message-template')}
                        </Title>
                        {isEdit ? (
                            <Actions>
                                <Button
                                    size="sm"
                                    type="button"
                                    variant={ButtonVariant.Outline}
                                    disabled={model.state.pending}
                                    onClick={() => {
                                        reset(formValues);
                                        setIsEdit(false);
                                    }}
                                >
                                    {t('actions.cancel')}
                                </Button>
                                <Button
                                    size="sm"
                                    type="submit"
                                    form="message-template-form"
                                    isLoading={model.state.pending}
                                    disabled={model.state.pending}
                                >
                                    {t('actions.save')}
                                </Button>
                            </Actions>
                        ) : (
                            <Actions>
                                <Button
                                    size="sm"
                                    type="button"
                                    variant={ButtonVariant.Outline}
                                    disabled={!messageTemplate || model.state.pending}
                                    onClick={() => setIsEdit(true)}
                                >
                                    {t('actions.edit')}
                                </Button>
                            </Actions>
                        )}
                        {status ? (
                            <StatusWrapper color={statusColor(status.code)}>
                                {localizeTitle(status.title, i18n.language)}
                            </StatusWrapper>
                        ) : null}
                    </MessagesHeader>
                </HeaderCard>

                <Form id="message-template-form" onSubmit={handleSubmit(onSubmit)} noValidate>
                    <SectionCard>
                        <InfoGrid>
                            {isEdit ? (
                                <>
                                    <Field>
                                        <Controller
                                            name="channel"
                                            control={control}
                                            render={({onChange, value}) => (
                                                <Select
                                                    size="sm"
                                                    menuPlacement="top"
                                                    label={t(
                                                        'page_audit.page_document-templates.channel',
                                                    )}
                                                    options={channelOptions}
                                                    value={channelOptions.find(
                                                        (item) => item.value === value,
                                                    )}
                                                    onChange={(option: unknown) =>
                                                        onChange(
                                                            (option as SelectOption<string> | null)
                                                                ?.value ?? '',
                                                        )
                                                    }
                                                    hasError={Boolean(errors.channel)}
                                                    isRequired
                                                    disabled={model.state.pending}
                                                />
                                            )}
                                        />
                                        {errors.channel && (
                                            <FieldError>{errors.channel.message}</FieldError>
                                        )}
                                    </Field>
                                    <Field>
                                        <Controller
                                            name="language"
                                            control={control}
                                            render={({onChange, value}) => (
                                                <Select
                                                    size="sm"
                                                    menuPlacement="top"
                                                    label={t(
                                                        'page_audit.page_document-templates.language',
                                                    )}
                                                    options={languageOptions}
                                                    value={languageOptions.find(
                                                        (item) => item.value === value,
                                                    )}
                                                    onChange={(option: unknown) =>
                                                        onChange(
                                                            (option as SelectOption<string> | null)
                                                                ?.value ?? '',
                                                        )
                                                    }
                                                    hasError={Boolean(errors.language)}
                                                    isRequired
                                                    disabled={model.state.pending}
                                                />
                                            )}
                                        />
                                        {errors.language && (
                                            <FieldError>{errors.language.message}</FieldError>
                                        )}
                                    </Field>
                                    <Field>
                                        <Controller
                                            name="subject"
                                            control={control}
                                            render={({onChange, value, name}) => (
                                                <Input
                                                    size="sm"
                                                    label={t(
                                                        'page_audit.page_document-templates.subject',
                                                    )}
                                                    name={name}
                                                    value={value}
                                                    onChange={onChange}
                                                    hasError={Boolean(errors.subject)}
                                                    isRequired
                                                    disabled={model.state.pending}
                                                />
                                            )}
                                        />
                                        {errors.subject && (
                                            <FieldError>{errors.subject.message}</FieldError>
                                        )}
                                    </Field>
                                    <Field>
                                        <Controller
                                            name="bodyContentType"
                                            control={control}
                                            render={({onChange, value}) => (
                                                <Select
                                                    size="sm"
                                                    menuPlacement="top"
                                                    label={t(
                                                        'page_audit.page_document-templates.body-content-type',
                                                    )}
                                                    options={contentTypeOptions}
                                                    value={contentTypeOptions.find(
                                                        (item) => item.value === value,
                                                    )}
                                                    onChange={(option: unknown) =>
                                                        onChange(
                                                            (option as SelectOption<string> | null)
                                                                ?.value ?? '',
                                                        )
                                                    }
                                                    hasError={Boolean(errors.bodyContentType)}
                                                    isRequired
                                                    disabled={model.state.pending}
                                                />
                                            )}
                                        />
                                        {errors.bodyContentType && (
                                            <FieldError>
                                                {errors.bodyContentType.message}
                                            </FieldError>
                                        )}
                                    </Field>
                                    <Field>
                                        <Controller
                                            name="body"
                                            control={control}
                                            render={({onChange, value, name}) => (
                                                <Textarea
                                                    label={t(
                                                        'page_audit.page_document-templates.body',
                                                    )}
                                                    name={name}
                                                    value={value}
                                                    onChange={onChange}
                                                    style={{height: 500}}
                                                    hasError={Boolean(errors.body)}
                                                    isRequired
                                                    disabled={model.state.pending}
                                                />
                                            )}
                                        />
                                        {errors.body && (
                                            <FieldError>{errors.body.message}</FieldError>
                                        )}
                                    </Field>
                                    <Field>
                                        <Controller
                                            name="isEnabled"
                                            control={control}
                                            render={({onChange, value, name}) => (
                                                <CheckboxRow>
                                                    <Checkbox
                                                        name={name}
                                                        checked={Boolean(value)}
                                                        disabled={model.state.pending}
                                                        onChange={(event) =>
                                                            onChange(event.target.checked)
                                                        }
                                                    />
                                                    {t('actions.activate')}
                                                </CheckboxRow>
                                            )}
                                        />
                                    </Field>
                                </>
                            ) : (
                                <>
                                    <InfoItem>
                                        <InfoLabel>
                                            {t('page_audit.page_document-templates.channel')}
                                        </InfoLabel>
                                        <InfoValue>
                                            {localizeTitle(
                                                messageTemplate?.channel?.title,
                                                i18n.language,
                                            )}
                                        </InfoValue>
                                    </InfoItem>
                                    <InfoItem>
                                        <InfoLabel>
                                            {t('page_audit.page_document-templates.language')}
                                        </InfoLabel>
                                        <InfoValue>
                                            {localizeTitle(language?.title, i18n.language)}
                                        </InfoValue>
                                    </InfoItem>
                                    <InfoItem>
                                        <InfoLabel>
                                            {t('page_audit.page_document-templates.subject')}
                                        </InfoLabel>
                                        <InfoValue>{messageTemplate?.subject || EMPTY}</InfoValue>
                                    </InfoItem>
                                    <InfoItem>
                                        <InfoLabel>
                                            {t(
                                                'page_audit.page_document-templates.body-content-type',
                                            )}
                                        </InfoLabel>
                                        <InfoValue>
                                            {localizeTitle(
                                                messageTemplate?.bodyContentType?.title,
                                                i18n.language,
                                            )}
                                        </InfoValue>
                                    </InfoItem>
                                    <InfoItem>
                                        <InfoLabel>
                                            {t('page_audit.page_document-templates.body')}
                                        </InfoLabel>
                                        <BodyPreview>{messageTemplate?.body || EMPTY}</BodyPreview>
                                        {messageTemplate ? (
                                            <Actions>
                                                <Button
                                                    size="sm"
                                                    type="button"
                                                    variant={ButtonVariant.Outline}
                                                    onClick={() => setIsPreviewOpen(true)}
                                                >
                                                    {t('actions.show')}
                                                </Button>
                                            </Actions>
                                        ) : null}
                                    </InfoItem>
                                </>
                            )}
                        </InfoGrid>
                    </SectionCard>
                </Form>

                <SectionCard>
                    <MessagesHeader>
                        <Title size={3}>
                            {t('page_audit.page_document-templates.attachments')}
                        </Title>
                    </MessagesHeader>
                    <Table
                        columns={contentColumns(t)}
                        data={contents}
                        isLoading={model.state.pending}
                        pageCount={Math.ceil(
                            (model.state.contentTemplates?.total ?? 0) /
                                model.state.contentPageSize,
                        )}
                        pageIndex={model.state.contentPageNumber}
                        totalRowsCount={model.state.contentTemplates?.total ?? 0}
                        onPageChange={model.contentPageNumberChanged}
                        onPageSizeChange={model.contentPageSizeChanged}
                    />
                </SectionCard>

                <Modal
                    show={isPreviewOpen}
                    onClose={() => setIsPreviewOpen(false)}
                    title={template ? localizeTitle(template.title, i18n.language) : undefined}
                    modalWidth={3}
                >
                    <ModalFrame title="message-template-preview" srcDoc={messageTemplate?.body} />
                </Modal>
            </Wrapper>
        </ContentContainer>
    );
};
